import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Search, Package, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ProductLookupDropdown — v2.5.833
 *
 * A search-as-you-type dropdown that pulls from GET /api/sales/products/lookup
 * and calls `onSelect(product)` when the user picks one. Purpose: eliminate
 * manual typing of product names on Proposal / Quote / Invoice line items so
 * every quoted line links back to an actual Product record (which means the
 * Konnect Marketplace can eventually list them + inventory tracks correctly +
 * ROI attribution stays clean).
 *
 * Props:
 *   onSelect(product)   Called with the picked product row
 *   placeholder         Input placeholder text
 *   value               Currently selected product's display name (controlled)
 *   showThumb           Show product thumbnail (default true)
 *   className           Extra classes on the wrapper
 *   testIdPrefix        data-testid prefix (default 'product-lookup')
 *
 * The `q` search runs against name + SKU. Debounced 250ms. Escape / click-out
 * closes the dropdown. Enter picks the highlighted result.
 */
export default function ProductLookupDropdown({
  onSelect,
  placeholder = 'Search products by name or SKU…',
  value = '',
  showThumb = true,
  className = '',
  testIdPrefix = 'product-lookup',
}) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Search — debounced to keep API load reasonable
  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/sales/products/lookup`, {
        params: { q: q.trim(), limit: 25 },
      });
      setResults(r.data || []);
      setHighlighted(0);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, search]);

  // Close on click-out
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const pick = (product) => {
    setQuery(product.name);
    setOpen(false);
    if (onSelect) onSelect(product);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(results[highlighted]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const formatMoney = (n, cur) => {
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency: cur || 'USD' }).format(n || 0);
    } catch {
      return `${cur || ''} ${(n || 0).toFixed(2)}`;
    }
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef} data-testid={`${testIdPrefix}-wrapper`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="pl-9"
          data-testid={`${testIdPrefix}-input`}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
             data-testid={`${testIdPrefix}-panel`}>
          {results.length === 0 && !loading && (
            <div className="px-4 py-3 text-sm text-gray-500">
              {query ? 'No products match your search' : 'Start typing to search…'}
            </div>
          )}
          {results.map((p, idx) => (
            <button
              key={p.id}
              type="button"
              onClick={() => pick(p)}
              onMouseEnter={() => setHighlighted(idx)}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-emerald-50 border-b border-gray-100 last:border-0
                ${idx === highlighted ? 'bg-emerald-50' : ''}`}
              data-testid={`${testIdPrefix}-option-${p.id}`}
            >
              {showThumb && (
                <div className="w-10 h-10 rounded flex-shrink-0 bg-gray-100 flex items-center justify-center overflow-hidden">
                  {p.thumb
                    ? <img src={p.thumb} alt="" className="w-full h-full object-cover" />
                    : <Package className="h-5 w-5 text-gray-400" />}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-900 truncate">{p.name}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                  {p.sku && <span className="font-mono">{p.sku}</span>}
                  <span className="capitalize">{p.product_type || 'simple'}</span>
                  {p.unit && p.unit !== 'each' && <span>/ {p.unit}</span>}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-semibold text-sm text-emerald-700">
                  {formatMoney(p.price, p.currency)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
